import { RelatedContentItem } from '../../../api/types/research-related-content-response';
import { ANALYTICS_CONSTANTS, trackEDDL } from '../../../utilities/analytics';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { truncateCopy } from '../../../utilities/helpers';

const SLIDE_WIDTH_SM = 200;
const SLIDE_WIDTH_MD = 240;
const MOBILE_SLIDE_WIDTH = 256;

const StyledImg = styled('img')(() => ({
    objectFit: 'cover',
    height: '100%',
    width: '100%',
}));

/**
 * 2 line truncation styles using vendor-prefixed properties.
 * Supported across all major browsers.
 * See: https://caniuse.com/?search=line-clamp,
 * https://css-tricks.com/almanac/properties/l/line-clamp/
 */
const mobileArticleTitleTruncationStyles = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: '3.2rem',
};

interface IResearchArticlesTileProps {
    itemIndex: number;
    content: RelatedContentItem;
}

const handleResearchArticleClick = (event: React.MouseEvent, title: string, url: string) => {
    event.preventDefault();

    // Analytics
    trackEDDL({
        event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
        linkDetails: {
            name: title,
            position: 'Research',
        },
    });

    // Redirect
    window.location.href = url;
};

export const ResearchArticlesTile = ({ itemIndex, content }: IResearchArticlesTileProps) => {
    content.title = content.title || '';
    return (
        <Stack
            direction="column"
            spacing={1.5}
            sx={{
                width: { xs: SLIDE_WIDTH_SM, md: SLIDE_WIDTH_MD },
            }}
        >
            <ButtonBase
                href={content.url}
                onClick={(event) => handleResearchArticleClick(event, content.title, content.url)}
                aria-label={content.title}
                data-index={itemIndex}
                sx={({ palette }) => ({
                    width: '100%',
                    height: '315px',
                    borderRadius: 0.5,
                    ':focus': {
                        outline: '2px solid',
                        outlineColor: palette.primary.main,
                        outlineStyle: 'auto',
                    },
                    alignItems: 'initial',
                    overflow: 'visible !important',
                })}
            >
                <Stack spacing={1.5}>
                    <Paper
                        elevation={0}
                        sx={{
                            height: { xs: SLIDE_WIDTH_SM, md: SLIDE_WIDTH_MD },
                            aspectRatio: { xs: '1/1', md: '6/5' },
                            borderRadius: { xs: 1.5, md: 2 },
                            overflow: 'hidden',
                            ':hover': {
                                boxShadow: 3,
                            },
                        }}
                    >
                        <StyledImg loading="lazy" src={`${content.image.url}?w=405`} alt={`${content.title}`} />
                    </Paper>
                    <Stack sx={{ alignSelf: 'stretch' }}>
                        <Typography variant="caption" color="textSecondary" sx={{ display: '-webkit-box' }}>
                            {content.category}
                        </Typography>
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Typography> {truncateCopy(content.title, 65, true)}</Typography>
                        </Box>
                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                            <Typography> {truncateCopy(content.title, 45, true)}</Typography>
                        </Box>
                    </Stack>
                </Stack>
            </ButtonBase>
        </Stack>
    );
};

export const MobileResearchArticlesTile = ({ itemIndex, content }: IResearchArticlesTileProps) => {
    content.title = content.title || '';

    return (
        <Stack
            spacing={1.5}
            sx={{
                width: MOBILE_SLIDE_WIDTH,
            }}
        >
            <ButtonBase
                href={content.url}
                onClick={(event) => handleResearchArticleClick(event, content.title, content.url)}
                aria-label={content.title}
                data-index={itemIndex}
                sx={({ palette }) => ({
                    width: '100%',
                    height: '64px',
                    borderRadius: 0.5,
                    ':focus': {
                        outline: '2px solid',
                        outlineColor: palette.primary.main,
                        outlineStyle: 'auto',
                    },
                    alignItems: 'initial',
                    overflow: 'visible !important',
                })}
            >
                <Stack spacing={1} direction="row">
                    <Paper
                        elevation={0}
                        sx={{
                            height: '4rem',
                            width: '4rem',
                            flexShrink: 0,
                            borderRadius: 0.5,
                            overflow: 'hidden',
                            ':hover': {
                                boxShadow: 3,
                            },
                        }}
                    >
                        <StyledImg loading="lazy" src={`${content.image.url}?w=128`} alt={`${content.title}`} />
                    </Paper>
                    <Typography
                        sx={{
                            ...mobileArticleTitleTruncationStyles,
                            flex: 1,
                            minWidth: 0,
                            alignSelf: 'center',
                        }}
                    >
                        {content.title}
                    </Typography>
                </Stack>
            </ButtonBase>
        </Stack>
    );
};
